import React, { useId, useMemo, useState } from 'react';
import {
  Button,
  Callout,
  Dialog,
  EmptyState,
  Field,
  IconBuilding,
  IconPlus,
  Input,
  ListPage,
  Table,
  TableSkeletonRows,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Textarea,
} from '../../ui/v2';
import { api, apiEndpoints } from '../../utils/apiService';
import { type TableSortState, toggleSortState } from '../../utils/listOrdering';
import { TableSearch } from './TableSearch';
import { filterAndSortRows, rowSearchText } from './clientTable';
import {
  EMPTY_STORE_GROUP_FORM,
  type StoreGroup,
  type StoreGroupFormData,
  storeGroupFormFrom,
  storeGroupPayload,
  storeGroupResponseError,
  updateStoreGroupForm,
  validateStoreGroupForm,
} from './storeGroupsModel';

type GroupSortKey = 'id' | 'name' | 'billing_email' | 'knowledge_base_id';

const COLUMNS: {
  key: string;
  label: string;
  width?: number;
  sortKey?: GroupSortKey;
}[] = [
  { key: 'id', label: 'ID', width: 80, sortKey: 'id' },
  { key: 'name', label: 'Name', sortKey: 'name' },
  { key: 'billing_email', label: 'Billing Email', sortKey: 'billing_email' },
  {
    key: 'knowledge_base_id',
    label: 'Legacy Knowledge Base',
    sortKey: 'knowledge_base_id',
  },
  { key: 'actions', label: 'Actions', width: 96 },
];

const Placeholder: React.FC<{ value?: string | null }> = ({ value }) =>
  value ? <>{value}</> : <span className="text-v2-fg-3">—</span>;

const StoreGroupFormDialog: React.FC<{
  open: boolean;
  title: string;
  submitLabel: string;
  form: StoreGroupFormData;
  error: string;
  success: string;
  onChange: React.Dispatch<React.SetStateAction<StoreGroupFormData>>;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  onDismiss: () => void;
}> = ({
  open,
  title,
  submitLabel,
  form,
  error,
  success,
  onChange,
  onSubmit,
  onCancel,
  onDismiss,
}) => {
  const formId = useId();
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    onChange((prev) => updateStoreGroupForm(prev, name, value));
  };

  return (
    <Dialog
      open={open}
      onClose={onDismiss}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form={formId}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field label="Name">
          {(id, describedBy) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
            />
          )}
        </Field>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Owner User ID" optional>
            {(id, describedBy) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="number"
                name="owner"
                value={form.owner ?? ''}
                onChange={handleChange}
                min="1"
              />
            )}
          </Field>
          <Field label="Billing Email" optional>
            {(id, describedBy) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="email"
                name="billing_email"
                value={form.billing_email}
                onChange={handleChange}
              />
            )}
          </Field>
        </div>
        <Field label="Notes" optional>
          {(id, describedBy) => (
            <Textarea
              id={id}
              aria-describedby={describedBy}
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={3}
            />
          )}
        </Field>
        {error && <Callout tone="danger">{error}</Callout>}
        {success && (
          <p role="status" className="text-v2-12 text-v2-success">
            {success}
          </p>
        )}
      </form>
    </Dialog>
  );
};

const StoreGroupsTab: React.FC<{
  storeGroups: StoreGroup[];
  loading: boolean;
  onChanged: () => void;
}> = ({ storeGroups, loading, onChanged }) => {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<TableSortState<GroupSortKey>>({
    key: null,
    direction: 'asc',
  });
  const visibleGroups = useMemo(
    () =>
      filterAndSortRows(
        storeGroups,
        search,
        (group) =>
          rowSearchText([
            group.id,
            group.name,
            group.billing_email,
            group.knowledge_base_id,
          ]),
        sort,
        (group, key) => group[key] ?? '',
      ),
    [search, sort, storeGroups],
  );
  const filtering = search.trim().length > 0;

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<StoreGroupFormData>(
    EMPTY_STORE_GROUP_FORM,
  );
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<StoreGroupFormData>(
    EMPTY_STORE_GROUP_FORM,
  );
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');

  const openCreate = () => {
    setCreateForm(EMPTY_STORE_GROUP_FORM);
    setCreateError('');
    setCreateSuccess('');
    setIsCreateOpen(true);
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError('');
    setCreateSuccess('');
    const invalid = validateStoreGroupForm(createForm);
    if (invalid) {
      setCreateError(invalid);
      return;
    }

    try {
      const response = await api.post(
        apiEndpoints.createStoreGroup(),
        storeGroupPayload(createForm),
      );
      if (response && response.ok) {
        const data = await response.json();
        setCreateSuccess(`Store group "${data.name}" created.`);
        setCreateForm(EMPTY_STORE_GROUP_FORM);
        onChanged();
      } else if (response) {
        const data = await response.json().catch(() => ({}));
        setCreateError(
          storeGroupResponseError(data, 'Failed to create store group'),
        );
      } else {
        setCreateError('Failed to create store group');
      }
    } catch {
      setCreateError('Failed to create store group');
    }
  };

  const openEdit = (group: StoreGroup) => {
    setEditId(group.id);
    setEditForm(storeGroupFormFrom(group));
    setEditError('');
    setEditSuccess('');
    setIsEditOpen(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editId) return;
    setEditError('');
    setEditSuccess('');
    const invalid = validateStoreGroupForm(editForm);
    if (invalid) {
      setEditError(invalid);
      return;
    }

    try {
      const response = await api.patch(
        apiEndpoints.updateStoreGroup(editId),
        storeGroupPayload(editForm),
      );
      if (response && response.ok) {
        const data = await response.json();
        setEditSuccess(`Store group "${data.name}" updated.`);
        onChanged();
        setTimeout(() => setIsEditOpen(false), 800);
      } else if (response) {
        const data = await response.json().catch(() => ({}));
        setEditError(
          storeGroupResponseError(data, 'Failed to update store group'),
        );
      } else {
        setEditError('Failed to update store group');
      }
    } catch {
      setEditError('Failed to update store group');
    }
  };

  return (
    <>
      <ListPage
        title="Store Groups"
        toolbar={
          <div className="flex min-h-11 items-center px-4 py-2 lg:px-6">
            <TableSearch
              label="Search store groups"
              placeholder="Search name, billing email, or knowledge base…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        }
        actions={
          <Button
            variant="primary"
            leading={<IconPlus size={14} />}
            onClick={openCreate}
          >
            Create
          </Button>
        }
      >
        {!loading && visibleGroups.length === 0 ? (
          <EmptyState
            icon={<IconBuilding />}
            title={
              filtering
                ? 'No store groups match that search'
                : "You don't have any store groups at the moment"
            }
            description={
              filtering
                ? 'Try a group name, billing email, or knowledge base id.'
                : "When store groups appear, they'll show up here."
            }
          />
        ) : (
          <>
            {loading && (
              <p role="status" className="sr-only">
                Loading store groups…
              </p>
            )}
            <div className="v2-scroll overflow-x-auto">
              <Table fixed className="min-w-[900px]">
                <THead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <TH
                        key={column.key}
                        width={column.width}
                        sortable={Boolean(column.sortKey) && !loading}
                        active={
                          Boolean(column.sortKey) && sort.key === column.sortKey
                        }
                        dir={sort.direction}
                        onSort={() =>
                          column.sortKey &&
                          setSort((current) =>
                            toggleSortState(current, column.sortKey!),
                          )
                        }
                      >
                        {column.label}
                      </TH>
                    ))}
                  </tr>
                </THead>
                <TBody>
                  {loading ? (
                    <TableSkeletonRows cols={COLUMNS.length} />
                  ) : (
                    visibleGroups.map((group) => (
                      <TR key={group.id}>
                        <TD num className="font-medium">
                          {group.id}
                        </TD>
                        <TD truncate className="font-medium">
                          {group.name}
                        </TD>
                        <TD truncate>
                          <Placeholder value={group.billing_email} />
                        </TD>
                        <TD truncate num className="text-v2-fg-2">
                          <Placeholder value={group.knowledge_base_id} />
                        </TD>
                        <TD>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="-ml-2.5"
                            onClick={() => openEdit(group)}
                          >
                            Edit
                          </Button>
                        </TD>
                      </TR>
                    ))
                  )}
                </TBody>
              </Table>
            </div>
          </>
        )}
      </ListPage>

      <StoreGroupFormDialog
        open={isCreateOpen}
        title="Create Store Group"
        submitLabel="Create Store Group"
        form={createForm}
        error={createError}
        success={createSuccess}
        onChange={setCreateForm}
        onSubmit={handleCreateSubmit}
        onCancel={() => setIsCreateOpen(false)}
        onDismiss={() => {
          setIsCreateOpen(false);
          setCreateError('');
          setCreateSuccess('');
        }}
      />

      <StoreGroupFormDialog
        open={isEditOpen}
        title="Edit Store Group"
        submitLabel="Save Changes"
        form={editForm}
        error={editError}
        success={editSuccess}
        onChange={setEditForm}
        onSubmit={handleEditSubmit}
        onCancel={() => setIsEditOpen(false)}
        onDismiss={() => {
          setIsEditOpen(false);
          setEditError('');
          setEditSuccess('');
        }}
      />
    </>
  );
};

export default StoreGroupsTab;
